/**
 * use-platform-invoices.ts — Platform → Hoá đơn điện tử (super-admin): hoá đơn SePay của đơn mua gói
 * mọi tổ chức. Gọi qua `platformApi` (token platform riêng).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import type { InvoiceInfo, InvoiceStatus } from '@/hooks/use-billing'

export interface PlatformInvoice {
  status: InvoiceStatus
  info: InvoiceInfo | null
  referenceCode: string | null
  invoiceNo: string | null
  pdfUrl: string | null
  xmlUrl: string | null
  error: string | null
  issuedAt: string | null
  requestedAt: string | null
  attempts: number
  processing: boolean
  unconfirmed: boolean
}

export interface PlatformInvoiceRow {
  orderId: string
  org: { id: string; name: string } | null
  planId: string
  planName: string
  amount: number
  orderStatus: string
  paymentCode: string | null
  paidAt: string | null
  createdAt: string
  invoice: PlatformInvoice
}

export interface PlatformInvoiceList {
  total: number
  page: number
  limit: number
  counts: Partial<Record<InvoiceStatus, number>>
  items: PlatformInvoiceRow[]
}

export interface EInvoiceConfigStatus {
  configured: boolean
  env: 'sandbox' | 'production' | null
  missing: string[]
  templateCode: string | null
  invoiceSeries: string | null
  providerAccountId: string | null
  vatRate: number
  priceIncludesVat: boolean
}

export interface InvoiceFilter {
  status?: string
  orgId?: string
  from?: string
  to?: string
  q?: string
  page?: number
  limit?: number
}

export function usePlatformInvoices(f: InvoiceFilter) {
  return useQuery<PlatformInvoiceList>({
    queryKey: ['platform', 'billing', 'invoices', f],
    queryFn: async () => {
      const { data } = await platformApi.get('/platform/billing/invoices', { params: f })
      return data
    },
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  })
}

export function useEInvoiceConfig() {
  return useQuery<EInvoiceConfigStatus>({
    queryKey: ['platform', 'billing', 'invoices', 'config'],
    queryFn: async () => {
      const { data } = await platformApi.get('/platform/billing/invoices/config')
      return data
    },
    staleTime: 5 * 60_000,
  })
}

export function useRetryInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ orderId, force }: { orderId: string; force?: boolean }) => {
      const { data } = await platformApi.post(`/platform/billing/invoices/${orderId}/retry`, { force: !!force })
      return data as PlatformInvoice
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'billing', 'invoices'] }),
  })
}

/** Tải file qua server (file → lưu; link SePay → mở tab mới). */
export async function downloadPlatformInvoice(orderId: string, type: 'pdf' | 'xml'): Promise<void> {
  const res = await platformApi.get(`/platform/billing/invoices/${orderId}/file`, { params: { type }, responseType: 'blob' })
  const blob = res.data as Blob
  if (blob.type.includes('json')) {
    const { url } = JSON.parse(await blob.text()) as { url?: string }
    if (url) window.open(url, '_blank', 'noopener')
    return
  }
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `hoa-don-${orderId.slice(0, 8)}.${type}`
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
}
